// Loads the shared Guru user-action recorder (gurucloudai kanban b91c17bf)
// for the orders portal, once per page lifetime.
//
// One implementation for every product: the script is fetched from
// GuruCloudAI, never vendored into this bundle. It authenticates with the
// SAME widget_embed_tokens row the feedback widget uses (GURU_WIDGET_TOKEN;
// allowed origins api.chemmasters.net + api-staging.chemmasters.net), so no
// second token exists to rotate.
//
// Screens are set explicitly (data-manual-screens): React Router owns the
// route templates, see hooks/useGuruRecorder.ts. No user is passed on the tag:
// the bridge calls GuruRecorder.setUser once the auth context resolves.

import { GURU_WIDGET_TOKEN } from './guruWidgetLoader';
import type { GuruRecorderApi } from '../types/guru-recorder';

export const GURU_RECORDER_PRODUCT = 'chemmasters_orders';
export const GURU_RECORDER_SCRIPT_URL = 'https://www.gurucloudai.com/static/js/guru-recorder.js';
const SCRIPT_ID = 'guru-recorder-script';

type ReadyListener = (api: GuruRecorderApi) => void;
const _readyListeners = new Set<ReadyListener>();

/** The live recorder API, or null until the script has loaded. */
export function getGuruRecorder(): GuruRecorderApi | null {
  return typeof window !== 'undefined' && window.GuruRecorder ? window.GuruRecorder : null;
}

/**
 * Run `listener` as soon as the recorder is available (immediately when it
 * already is). Returns an unsubscribe function.
 */
export function onGuruRecorderReady(listener: ReadyListener): () => void {
  const api = getGuruRecorder();
  if (api) {
    listener(api);
    return () => {};
  }
  _readyListeners.add(listener);
  return () => {
    _readyListeners.delete(listener);
  };
}

function notifyReady(): void {
  const api = getGuruRecorder();
  if (!api) return;
  const listeners = [..._readyListeners];
  _readyListeners.clear();
  for (const listener of listeners) {
    try {
      listener(api);
    } catch {
      // recording must never break the page
    }
  }
}

/**
 * Inject the recorder script tag once. `initialScreen` is the route template
 * of the page that is open when the script loads, so the first page_view row
 * already carries a template instead of the masked path.
 */
export function ensureGuruRecorder(initialScreen?: string): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SCRIPT_ID)) {
    notifyReady();
    return;
  }
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = GURU_RECORDER_SCRIPT_URL;
  script.async = true;
  script.setAttribute('data-product', GURU_RECORDER_PRODUCT);
  script.setAttribute('data-token', GURU_WIDGET_TOKEN);
  script.setAttribute('data-manual-screens', 'true');
  if (initialScreen) script.setAttribute('data-screen', initialScreen);
  script.addEventListener('load', notifyReady);
  document.body.appendChild(script);
}

/** Test-only: drop the injected tag and any pending listeners. */
export function _resetGuruRecorderForTests(): void {
  _readyListeners.clear();
  document.getElementById(SCRIPT_ID)?.remove();
}
